/** Query-side editor for comparing one ship over two to four fixed UTC periods. */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeDataSource,
  activeSection,
  activePreset,
  activeTab,
  comparisonShips,
  endDate,
  isRootUser,
  myPermissions,
  selectedShip,
  shipDateRange,
  startDate,
} from '../../state/store.ts';
import { plotState } from '../../state/plot-state.ts';
import { Atom } from '../../state/store.ts';
import {
  MAX_COMPARISON_PERIODS,
  REFERENCE_PERIOD_ID,
  addComparisonPeriod,
  comparisonHasDifferentDurations,
  comparisonHasOverlap,
  comparisonIssues,
  comparisonReady,
  expandedPeriodId,
  formatPeriodDuration,
  normalizeUtcDatetime,
  periodComparison,
  periodComparisonNotice,
  periodAbove,
  periodDurationSeconds,
  periodLetter,
  previousPeriod,
  removeComparisonPeriod,
  resolveComparisonPeriods,
  setReferenceLabel,
  startPeriodComparison,
  stopPeriodComparison,
  updateComparisonPeriod,
  utcEpochSeconds,
  type PlotPanelSpec,
} from '../../state/period-comparison.ts';
import { applyDatePreset, setPresetActive } from './date-presets.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { rerenderChartFromLast, stopDisplayingPeriodComparison } from '../plot/plot-actions.ts';
import { sourceHasHistory, sourceLabel } from '../../state/data-sources.ts';

/**
 * The preset A was last set from, so its chip lights up blue like the main
 * date range's. The global preset has to read `custom` while comparing (the
 * periods are fixed snapshots), so this remembers it - and forgets it the
 * moment A's dates are edited by hand. Module-level, because the editor
 * element is replaced when comparison starts.
 */
const referencePreset = new Atom<string | null>(null);

/** The same everyday windows as the main date range, plus All data. */
const REFERENCE_PRESETS = [
  ['24h', '24h'],
  ['7d', '7d'],
  ['1mo', '1M'],
  ['3m', '3M'],
  ['6m', '6M'],
  ['1y', '1Y'],
  ['max', 'All data'],
] as const;

function dateTimeLabel(value: string): string {
  const seconds = utcEpochSeconds(value);
  if (seconds == null) return value || 'Invalid date';
  return new Date(seconds * 1000).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
}

function availabilityNotice(period: PlotPanelSpec): string | null {
  if (
    period.id !== REFERENCE_PERIOD_ID &&
    normalizeUtcDatetime(period.start) === normalizeUtcDatetime(startDate.value) &&
    normalizeUtcDatetime(period.end) === normalizeUtcDatetime(endDate.value)
  ) {
    return 'Same interval as A; the response may be reused.';
  }
  const available = shipDateRange.value;
  const start = utcEpochSeconds(period.start);
  const end = utcEpochSeconds(period.end);
  const min = utcEpochSeconds(available?.min_date ?? '');
  const max = utcEpochSeconds(available?.max_date ?? '');
  if (start == null || end == null || min == null || max == null) return null;
  if (end <= min || start >= max) return 'Outside the available data range; an empty panel is allowed.';
  if (start < min || end > max) return 'Partly outside the available data range; the requested dates will not be changed.';
  return null;
}

@customElement('period-comparison-editor')
export class PeriodComparisonEditor extends LightElement {
  private store = new StoreController(this, [
    periodComparison,
    expandedPeriodId,
    periodComparisonNotice,
    referencePreset,
    startDate,
    endDate,
    isRootUser,
    myPermissions,
    activePreset,
    selectedShip,
    comparisonShips,
    activeDataSource,
    activeSection,
    activeTab,
    plotState,
    shipDateRange,
  ]);

  private get inBuilder(): boolean {
    return !!this.closest('chart-builder');
  }

  private blockedReason(): string | null {
    if (this.inBuilder) return 'Period comparisons open in Explore and cannot be added to dashboards yet.';
    if (activeSection.value !== 'explore') return 'Open Explore → Plot to compare periods.';
    if (activeTab.value !== 'plot') return 'Open Plot to compare periods.';
    if (!sourceHasHistory(activeDataSource.value)) return `Period comparison needs history; ${sourceLabel(activeDataSource.value)} keeps a rolling window.`;
    if (!selectedShip.value) return 'Select one ship to compare periods.';
    if (comparisonShips.value.length) return 'Select one ship to compare periods.';
    if (plotState.value.plot_type !== 'line' && plotState.value.plot_type !== 'scatter') {
      return 'Available for Line and Scatter.';
    }
    const reference = {
      id: REFERENCE_PERIOD_ID,
      label: 'Reference',
      start: startDate.value,
      end: endDate.value,
    };
    return periodDurationSeconds(reference) == null ? 'Enter a valid date range first.' : null;
  }

  private activate(): void {
    if (this.blockedReason()) return;
    // The preset A was on as comparison starts is the one its chip shows.
    const preset = activePreset.value;
    referencePreset.set(preset !== 'custom' ? preset : null);
    if (!startPeriodComparison()) return;
    comparisonShips.set([]);
    // A rolling preset becomes a fixed snapshot as comparison starts.
    setPresetActive('custom');
    scheduleEstimate();
    syncToUrl();
  }

  private stop(): void {
    stopPeriodComparison();
    stopDisplayingPeriodComparison();
    scheduleEstimate();
    syncToUrl();
  }

  private changeReference(which: 'start' | 'end', value: string): void {
    (which === 'start' ? startDate : endDate).set(value);
    setPresetActive('custom');
    referencePreset.set(null);
    scheduleEstimate();
    syncToUrl();
  }

  private applyReferencePreset(id: typeof REFERENCE_PRESETS[number][0]): void {
    applyDatePreset(id);
    // Presets are actions in this editor: keep their resolved UTC bounds fixed.
    setPresetActive('custom');
    referencePreset.set(id);
    syncToUrl();
  }

  /**
   * Re-anchor a period to end where the row above it starts, keeping that
   * row's length - C goes right before B, not before A. Only the dates move;
   * a label the user typed is theirs.
   */
  private beforeAbove(period: PlotPanelSpec, index: number): void {
    const above = periodAbove(index);
    const moved = above ? previousPeriod(above, period.id, period.label) : null;
    if (!moved) return;
    updateComparisonPeriod(period.id, { start: moved.start, end: moved.end });
    syncToUrl();
  }

  private matchReferenceDuration(period: PlotPanelSpec): void {
    const duration = periodDurationSeconds({ start: startDate.value, end: endDate.value });
    const start = utcEpochSeconds(period.start);
    if (duration == null || start == null) return;
    updateComparisonPeriod(period.id, {
      end: new Date((start + duration) * 1000).toISOString().slice(0, 19),
    });
    syncToUrl();
  }

  /** A period's length in words, by position ("30 days"), or "same length". */
  private durationOf(index: number): string {
    const period = resolveComparisonPeriods()[index];
    const seconds = period ? periodDurationSeconds(period) : null;
    return seconds == null ? 'same length' : formatPeriodDuration(seconds);
  }

  private fieldIssue(periodId: string, field: 'start' | 'end' | 'label'): string | null {
    return comparisonIssues().find((issue) => issue.periodId === periodId && issue.field === field)?.message ?? null;
  }

  private renderSummary(period: PlotPanelSpec, index: number): TemplateResult {
    const expanded = expandedPeriodId.value === period.id;
    const availability = availabilityNotice(period);
    return html`
      <article class="period-row ${expanded ? 'is-expanded' : ''}">
        <button
          class="period-summary"
          title=${expanded ? 'Collapse' : 'Edit this period'}
          aria-expanded=${expanded}
          aria-controls=${`period-editor-${period.id}`}
          @click=${() => expandedPeriodId.set(expanded ? null : period.id)}
        >
          <span class="period-badge">${periodLetter(index)}</span>
          <span class="period-summary-main">
            <strong>${period.label || `Period ${periodLetter(index)}`}</strong>
            <span>${dateTimeLabel(period.start)} → ${dateTimeLabel(period.end)}</span>
          </span>
          <span class="period-duration">${formatPeriodDuration(periodDurationSeconds(period))}</span>
          <span class="period-chevron" aria-hidden="true"></span>
        </button>
        ${availability ? html`<p class="period-notice">${availability}</p>` : nothing}
        <div
          class="period-editor-collapse ${expanded ? 'is-open' : ''}"
          aria-hidden=${expanded ? 'false' : 'true'}
          .inert=${!expanded}
        >
          <div class="period-editor-collapse-inner">
            ${this.renderFields(period, index)}
          </div>
        </div>
      </article>
    `;
  }

  /**
   * The next period, as a row of its own: what it will be called and exactly
   * which dates it will cover, before it is added - so adding one is a choice
   * you can read, not a guess you then go and inspect.
   */
  private renderAdd(periods: PlotPanelSpec[]): TemplateResult {
    if (periods.length >= MAX_COMPARISON_PERIODS) {
      return html`<p class="period-add-full">All ${MAX_COMPARISON_PERIODS} periods in use.</p>`;
    }
    const letter = periodLetter(periods.length);
    const last = periods[periods.length - 1];
    const preview = last ? previousPeriod(last) : null;
    return html`<button
      class="period-add"
      @click=${() => {
        addComparisonPeriod();
        syncToUrl();
      }}
    >
      <span class="period-add-badge" aria-hidden="true">+</span>
      <span class="period-summary-main">
        <strong>Add period ${letter}</strong>
        <span>
          ${preview
            ? html`${dateTimeLabel(preview.start)} → ${dateTimeLabel(preview.end)}`
            : 'Right before the last period'}
        </span>
      </span>
      <span class="period-duration">
        ${preview ? formatPeriodDuration(periodDurationSeconds(preview)) : ''}
      </span>
    </button>`;
  }

  private renderFields(period: PlotPanelSpec, index: number): TemplateResult {
    const isA = period.id === REFERENCE_PERIOD_ID;
    const startIssue = this.fieldIssue(period.id, 'start');
    const endIssue = this.fieldIssue(period.id, 'end');
    const labelIssue = this.fieldIssue(period.id, 'label');
    return html`
      <div class="period-editor" id=${`period-editor-${period.id}`}>
        ${isA
          ? html`<div class="period-presets">
              ${REFERENCE_PRESETS.map(([id, label]) => html`<button
                class="date-preset ${referencePreset.value === id ? 'active' : ''}"
                aria-pressed=${referencePreset.value === id ? 'true' : 'false'}
                @click=${() => this.applyReferencePreset(id)}
              >${label}</button>`)}
            </div>`
          : html`<div class="period-shortcuts">
              <button
                class="text-btn"
                title=${`Move ${periodLetter(index)} to the ${this.durationOf(index - 1)} right before ${periodLetter(index - 1)}`}
                @click=${() => this.beforeAbove(period, index)}
              >Right before ${periodLetter(index - 1)}</button>
              <button class="text-btn" @click=${() => this.matchReferenceDuration(period)}>Match A's length</button>
            </div>`}
        <div class="period-field-grid">
          <label for=${`period-start-${period.id}`}>
            Start
            <input
              id=${`period-start-${period.id}`}
              type="datetime-local"
              step="1"
              aria-invalid=${startIssue ? 'true' : 'false'}
              aria-describedby=${startIssue ? `period-start-error-${period.id}` : nothing}
              .value=${period.start}
              @change=${(event: Event) => {
                const value = (event.target as HTMLInputElement).value;
                if (isA) this.changeReference('start', value);
                else {
                  updateComparisonPeriod(period.id, { start: value });
                  syncToUrl();
                }
              }}
            />
            ${startIssue ? html`<span class="field-error" id=${`period-start-error-${period.id}`}>${startIssue}</span>` : nothing}
          </label>
          <label for=${`period-end-${period.id}`}>
            End
            <input
              id=${`period-end-${period.id}`}
              type="datetime-local"
              step="1"
              aria-invalid=${endIssue ? 'true' : 'false'}
              aria-describedby=${endIssue ? `period-end-error-${period.id}` : nothing}
              .value=${period.end}
              @change=${(event: Event) => {
                const value = (event.target as HTMLInputElement).value;
                if (isA) this.changeReference('end', value);
                else {
                  updateComparisonPeriod(period.id, { end: value });
                  syncToUrl();
                }
              }}
            />
            ${endIssue ? html`<span class="field-error" id=${`period-end-error-${period.id}`}>${endIssue}</span>` : nothing}
          </label>
        </div>
        <label for=${`period-label-${period.id}`}>
          Label <span class="save-optional">optional · 40 characters</span>
          <input
            id=${`period-label-${period.id}`}
            type="text"
            maxlength="40"
            aria-invalid=${labelIssue ? 'true' : 'false'}
            .value=${period.label}
            @input=${(event: Event) => {
              const label = (event.target as HTMLInputElement).value;
              if (isA) setReferenceLabel(label);
              else updateComparisonPeriod(period.id, { label });
              rerenderChartFromLast();
              syncToUrl();
            }}
          />
        </label>
        ${!isA
          ? html`<button class="period-remove text-btn" @click=${() => {
              // Removing the last extra period leaves A alone, still in
              // comparison mode - so the chart goes back to A's own plot.
              const leaving = periodComparison.value?.additional.length === 1;
              removeComparisonPeriod(period.id);
              if (leaving) stopDisplayingPeriodComparison();
              syncToUrl();
            }}>Remove period ${periodLetter(index)}</button>`
          : nothing}
      </div>
    `;
  }

  override render(): TemplateResult {
    const comparison = periodComparison.value;
    if (!comparison) {
      const blocked = this.blockedReason();
      return html`
        <div class="compare-entry">
          <span class="compare-entry-label">Date range <span class="utc-badge" title="All times are UTC">UTC</span></span>
          <span class="compare-entry-action" title=${blocked ?? 'Compare this ship across fixed UTC periods'}>
            <button class="compare-periods-btn" ?disabled=${!!blocked} @click=${() => this.activate()}>
              <svg class="compare-periods-icon" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M2 4.5h9m0 0L8.8 2.3M11 4.5 8.8 6.7M14 11.5H5m0 0 2.2-2.2M5 11.5l2.2 2.2" />
              </svg>
              Compare periods
            </button>
          </span>
        </div>
        ${periodComparisonNotice.value ? html`<p class="period-mode-notice">${periodComparisonNotice.value}</p>` : nothing}
      `;
    }

    const periods = resolveComparisonPeriods();
    return html`
      <div class="compare-entry">
        <span class="compare-entry-label">Periods <span class="utc-badge" title="All times are UTC">UTC</span></span>
        <button class="compare-periods-btn" @click=${() => this.stop()}>
          <svg class="compare-periods-icon" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
          Stop comparing
        </button>
      </div>
      <p class="period-fixed-note">
        ${comparisonReady()
          ? `Fixed periods · one ship · ${periods.length} panels`
          : 'Add a period to compare against A.'}
      </p>
      <div class="period-list">
        ${periods.map((period, index) => this.renderSummary(period, index))}
        ${this.renderAdd(periods)}
      </div>
      ${comparisonHasDifferentDurations(periods) ? html`<p class="period-advisory">Different durations</p>` : nothing}
      ${comparisonHasOverlap(periods) ? html`<p class="period-advisory">Some periods overlap; overlapping rows may appear in more than one panel.</p>` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'period-comparison-editor': PeriodComparisonEditor;
  }
}
